import React from "react";
import ReactDOM from "react-dom/client";
import "./styles/index.scss";

import { QueryClientProvider } from "@tanstack/react-query";
import { getPhotoUrl } from "./common/utils/getPhotoUrl";
import ExplorePage from "./pages/estateExplore/EstateExploreNew";
import AiWritePage from "./components/aiWrite/AiWrite";
import EstateManagePage from "./pages/estateManage/EstateManagePage";
import MembershipGuidePage from "./pages/membershipGuide/MembershipGuide";
import SaleReportPage from "./pages/saleReport/SaleReportPage";
import ChatPage from "./pages/messenger/components/ChatPage/ChatPage";
import ContactModal from "./pages/messenger/components/ContactModal/ContactModal";
import OwnerVerificationWidget from "./pages/ownerVerification/OwnerVerificationWidget";
import { TicketPurchaseWidget } from "./features/ticketPurchase";
import CouponModal from "./pages/coupon/CouponModal";
import { SCREENS } from "./pages/messenger/constants/constants";
import { queryClient } from "./pages/messenger/queryClient";
import { getTotalUnreadCount } from "./pages/messenger/stores/totalUnreadStore";
import {
  disconnectChatWebSocket,
  connectChatWebSocket as doConnectChatWebSocket,
} from "./pages/messenger/websocket/webSocketConnection";
import ChatNotificationToast from "./pages/messenger/components/shared/ChatNotificationToast/ChatNotificationToast";

const CHAT_NOTIFICATION_TOAST_ID = "react-chat-notification-toast";

function mountChatNotificationToast() {
  if (document.getElementById(CHAT_NOTIFICATION_TOAST_ID)) return;
  const el = document.createElement("div");
  el.id = CHAT_NOTIFICATION_TOAST_ID;
  document.body.appendChild(el);
  const root = ReactDOM.createRoot(el);
  root.render(
    <QueryClientProvider client={queryClient}>
      <ChatNotificationToast />
    </QueryClientProvider>
  );
}

// 채팅 WebSocket 연결
window.connectChatWebSocket = function connectChatWebSocket() {
  doConnectChatWebSocket();
  mountChatNotificationToast();
};
// 로그아웃 시 채팅 WebSocket 연결 해제
window.disconnectChatWebSocket = disconnectChatWebSocket;

// 미읽음 개수 — re.disco.www 배지 표시용
window.getTotalUnreadCount = getTotalUnreadCount;

// 개발용 WebSocket을 차단
if (process.env.NODE_ENV === "development") {
  console.log("⚠️ WebSocket 차단됨 (HMR, Fast Refresh 비활성화)");
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = { isDisabled: true }; // DevTools WebSocket도 차단
}
// 여러 개의 React 컴포넌트를 저장 (채팅은 별도 위젯 API로만 마운트)
const components = {
    EstateManage : EstateManagePage,
    Explore : ExplorePage,
    ExploreMobile : ExplorePage, // 통합 컴포넌트가 viewport 기준으로 모바일 레이아웃 자동 적용
    AiWrite : AiWritePage,
    MembershipGuide : MembershipGuidePage,
    SaleReport : SaleReportPage,
};

const rootInstances = {};
const updateReactComponentState = (elementId, newState) => {
  if (rootInstances[elementId] && rootInstances[elementId].setState) {
    rootInstances[elementId].setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  } else {
    console.error(`❌ Component with ID ${elementId} not found or setState is undefined!`);
  }
};

window.renderReactComponentByName = (componentName, elementId, props = {}) => {
  const Component = components[componentName];
  if (!Component) {
    console.error(`❌ Component ${componentName} not found!`);
    return;
  }

  let element = document.getElementById(elementId);
  if (!element) {
    element = document.createElement("div");
    element.id = elementId;
    element.className = "react-root";
    document.body.appendChild(element);
  }

  if (!rootInstances[elementId]) {
    const root = ReactDOM.createRoot(element);
    rootInstances[elementId] = {
      root,
      setState: null, // 상태 업데이트 함수 저장
    };
  }

  const ComponentWrapper = () => {
    const [state, setState] = React.useState({ history: "show" });

    // 상태 변경 함수를 저장
    rootInstances[elementId].setState = setState;

    return <Component {...props} state={state} />;
  };

  rootInstances[elementId].root.render(<ComponentWrapper />);
};

window.showReactComponentByName = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.style.display = ""; // Make it visible
    updateReactComponentState(elementId, { history: "show" });
  }
};

window.hideReactComponentByName = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    updateReactComponentState(elementId, { history: "hide" });
    element.style.display = "none"; // Hide it
  }
};

window.unmountReactComponentByName = (elementId) => {
  if (rootInstances[elementId]) {
    updateReactComponentState(elementId, { history: "destroy" });
    rootInstances[elementId].root.unmount();
    const element = document.getElementById(elementId);
    element.remove();
    delete rootInstances[elementId]; // 메모리에서 제거
  }
};

// --- 위젯 (history/state 래퍼 없이 이름으로 매핑, re.disco.live 등 연동용) ---
const CONTACT_MODAL_ROOT_ID = "contact-modal-root";
const CHAT_WIDGET_ROOT_ID = "chat-widget-root";

const widgets = {
  Chat: ChatPage,
  ContactModal,
  Coupon: CouponModal,
};

// 팝업 전용 위젯 (openPopup으로만 열림, renderWidgetByName과 별도)
const popupWidgets = {
  OwnerVerification: OwnerVerificationWidget,
  TicketPurchase: TicketPurchaseWidget,
};
const widgetRoots = {};

window.renderWidgetByName = (widgetName, elementId, props = {}) => {
  const Widget = widgets[widgetName];
  if (!Widget) {
    console.error(`❌ Widget "${widgetName}" not found!`);
    return;
  }
  const id =
    elementId ||
    (widgetName === "ContactModal"
      ? CONTACT_MODAL_ROOT_ID
      : `${widgetName.toLowerCase()}-widget-root`);
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement("div");
    element.id = id;
    const modalWidgets = ["ContactModal", "Coupon"];
    element.className = modalWidgets.includes(widgetName)
      ? "react-root"
      : "widget-container react-root";
    document.body.appendChild(element);
  }
  if (!widgetRoots[id]) {
    widgetRoots[id] = ReactDOM.createRoot(element);
  }
  let mergedProps = props;
  let registerChatHistory = null;
  if (widgetName === "Chat") {
    const chatId = id;
    const backHandlerRef = { current: null };
    let chatClosed = false;

    // chat_widget 등록 전 bh 기준 인덱스 (ContactModal 경유 시 _historyBaseIdx 우선 사용)
    const historyBaseIdx =
      props._historyBaseIdx !== undefined
        ? props._historyBaseIdx
        : window.bh
          ? window.bh.history.length - 1
          : -1;

    // chat_widget + 연관된 bh 항목(contact_modal 등)을 모두 정리
    const fullCloseHistory = () => {
      chatClosed = true;
      if (!window.bh) return;
      window.bh.close_history(historyBaseIdx);
      if (window.bh.history.length === 0) {
        window.app_popup_presense?.(false);
      }
    };

    registerChatHistory = () => {
      if (!window.bh) return;
      window.bh.setHistory("chat_widget", [
        {
          destroy: () => {
            if (typeof backHandlerRef.current === "function") {
              const reRegister = registerChatHistory;
              backHandlerRef.current(() => {
                // 명시적으로 닫힌 경우(overlay/X 클릭)에는 재등록 생략
                if (!chatClosed) setTimeout(reRegister, 0);
              });
            } else {
              fullCloseHistory();
              window.unmountWidgetByName(chatId);
            }
          },
        },
      ]);
    };

    mergedProps = {
      ...props,
      onCloseClick:
        props.onCloseClick ||
        (() => {

          fullCloseHistory();
          window.unmountWidgetByName(chatId);
        }),
      onRegisterBackHandler: (fn) => {
        backHandlerRef.current = fn;
      },
    };
  } else if (widgetName === "ContactModal") {
    const photoUrl = getPhotoUrl(props.photo, "agent");
    mergedProps = {
      name: props.name ?? "",
      phone: props.phone ?? "",
      photoUrl: photoUrl || "",
      auid: props.auid ?? "",
      onClose:
        props.onClose ||
        (() => {
          // onOpenChat이 먼저 호출되어 이미 언마운트된 경우 no-op
          if (!widgetRoots[id]) return;
          if (window.bh) window.bh.go_before();
          else window.unmountWidgetByName(id);
        }),
      onCallClick: typeof props.onCallClick === "function" ? props.onCallClick : undefined,
      onOpenChat:
        props.onOpenChat ||
        ((agentRoom) => {
          window.unmountWidgetByName(id);
          window.renderWidgetByName("Chat", CHAT_WIDGET_ROOT_ID, {
            rooms: [agentRoom],
            initialScreen: SCREENS.CHAT_ROOM,
            initialSelectedRoom: agentRoom,
            suid: props.suid ?? null,
            _historyBaseIdx: props._historyBaseIdx,
          });
        }),
    };
  }
  const doRender = () =>
    widgetRoots[id].render(
      <QueryClientProvider client={queryClient}>
        <Widget {...mergedProps} />
      </QueryClientProvider>
    );
  if (widgetName === "ContactModal" || widgetName === "Chat") {
    if (typeof window.connectChatWebSocket === "function") {
      window.connectChatWebSocket();
    }
    doRender();
    if (widgetName === "Chat") registerChatHistory?.();
  } else {
    doRender();
  }
};

window.unmountWidgetByName = (elementId) => {
  const id = elementId ?? CHAT_WIDGET_ROOT_ID;
  if (widgetRoots[id]) {
    widgetRoots[id].unmount();
    const element = document.getElementById(id);
    if (element) element.remove();
    delete widgetRoots[id];
  }
};

// 채팅 위젯 단축 API
window.renderChatWidget = (elementId, props = {}) => {
  window.renderWidgetByName("Chat", elementId || CHAT_WIDGET_ROOT_ID, props);
};
window.unmountChatWidget = (elementId) => {
  window.unmountWidgetByName(elementId || CHAT_WIDGET_ROOT_ID);
};

// ContactModal 단축 API (re.disco.www 문의하기 버튼 연동)
window.openContactModal = (name, phone, photo, auid, onCallClick, suid) => {
  // bh.setHistory("contact_modal") 호출 전 인덱스를 저장 → 채팅에서 "전체 닫기" 시 기준점
  const _historyBaseIdx = window.bh ? window.bh.history.length - 1 : -1;
  window.renderWidgetByName("ContactModal", CONTACT_MODAL_ROOT_ID, {
    name,
    phone: phone ?? "",
    photo,
    auid,
    onCallClick,
    suid,
    _historyBaseIdx,
  });
  if (window.bh) {
    window.bh.setHistory("contact_modal", [
      { destroy: () => window.unmountWidgetByName(CONTACT_MODAL_ROOT_ID) },
    ]);
  }
};
window.closeContactModal = () => {
  window.unmountWidgetByName(CONTACT_MODAL_ROOT_ID);
};

// --- 팝업형 위젯 (bh history 없이, 단순 마운트/언마운트) ---
const popupRoots = {};

/**
 * 팝업 위젯 열기 — 페이지 히스토리에 영향 없이 DOM에 마운트
 * @param {string} widgetName - widgets에 등록된 이름
 * @param {string} rootId     - DOM element id
 * @param {object} props      - 위젯 props (onClose는 자동 주입)
 * @param {function} onClose  - 외부 닫기 콜백 (optional)
 */
window.openPopup = (widgetName, rootId, props = {}, onClose) => {
  const Widget = popupWidgets[widgetName];
  if (!Widget) {
    console.error(`❌ Popup widget "${widgetName}" not found!`);
    return;
  }

  let element = document.getElementById(rootId);
  if (!element) {
    element = document.createElement("div");
    element.id = rootId;
    element.className = "react-popup-root";
    document.body.appendChild(element);
  }

  if (!popupRoots[rootId]) {
    popupRoots[rootId] = ReactDOM.createRoot(element);
  }

  const handleClose = () => {
    window.closePopup(rootId);
    onClose?.();
  };

  popupRoots[rootId].render(<Widget {...props} onClose={handleClose} />);
};

/** 인증권 구매 팝업 단축 API */
window.openTicketPurchase = (params, onClose) => {
  window.openPopup("TicketPurchase", "ticket-purchase-root", params || {}, onClose);
};

/** 팝업 위젯 닫기 — DOM에서 제거 */
window.closePopup = (rootId) => {
  if (popupRoots[rootId]) {
    popupRoots[rootId].unmount();
    const element = document.getElementById(rootId);
    if (element) element.remove();
    delete popupRoots[rootId];
  }
};


